import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { FC, useState } from 'react';
import { AutoCompleteSearchField } from '../../../../components/autocomplete/AutoCompleteSearchField';
import {
	EntityPropertySearchTag,
	IEntityPropertySearch,
} from '../../../../components/entities/EntityPropertySearchTag';
import { useTagsStorage } from '../../../../components/entities/tags/TagsEditor/hooks';
import { FeaturedAndRecentTags } from '../../../../components/entities/tags/TagsEditor/presentation';
import { SearchIcon } from '../../../../components/svgs/icons/SearchIcon';
import { navigation } from '../../../../styles/colors';
import { useCompanyTagFilter } from '../hooks/useCompanyTagFilter';
import { styleSheet } from './styles';

interface CompanyTagFiltersProps {
	className?: string;
	styles?: (StyleDeclarationValue | false)[];
	filter: Api.IBulkContactsRequest;
	onChangeFilter: (filter: Api.IBulkContactsRequest) => void;
}

export const CompanyTagFilters: FC<CompanyTagFiltersProps> = ({ className, styles = [], filter, onChangeFilter }) => {
	const { companyTag, setCompanyTag, clearCompanyTagFilter } = useCompanyTagFilter({
		filter,
		onChangeFilter,
	});

	const [isOpen, setIsOpen] = useState(false);
	const [inputValue, setInputValue] = useState('');
	const { recentSearches, updateTagsRecentSearches } = useTagsStorage();

	const handleTagSelected = (tag: string) => {
		updateTagsRecentSearches(tag);
		setCompanyTag(tag);
		setInputValue('');
	};

	const handleRemoveTag = () => {
		clearCompanyTagFilter();
	};

	const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setInputValue(e.target.value);
	};

	const emptyResults =
		recentSearches?.length > 0 ? (
			<FeaturedAndRecentTags
				onSelected={(tag: Api.IAccountTag) => handleTagSelected(tag?.name)}
				showNoResults={Boolean(inputValue)}
			/>
		) : undefined;

	// If a company tag is selected, show it with a remove button
	if (companyTag) {
		const search: IEntityPropertySearch = { value: companyTag };
		return (
			<div className={`${css(styleSheet.container, ...(styles?.filter(Boolean) || []))} ${className || ''}`}>
				<div className={css(styleSheet.fieldContainer)}>
					<label className={css(styleSheet.fieldLabel)}>Company Tag</label>
					<div className={css(styleSheet.selectedTagContainer)}>
						<EntityPropertySearchTag
							className={css(styleSheet.selectedTag)}
							search={search}
							onRemoveSearch={handleRemoveTag}
						/>
					</div>
				</div>
			</div>
		);
	}

	// Show the autocomplete search when no tag is selected
	return (
		<div className={`${css(styleSheet.container, ...(styles?.filter(Boolean) || []))} ${className || ''}`}>
			<div className={css(styleSheet.fieldContainer)}>
				<label className={css(styleSheet.fieldLabel)}>Company Tag</label>
				<AutoCompleteSearchField
					anchorClassName={css(styleSheet.searchBox)}
					clearSearchFieldAfterSelectingItem={true}
					dropdownContentClassName={css(styleSheet.autocompleteBox)}
					inputId='companyTagSearchInput'
					inputProps={{
						placeholder: 'Search tags',
						onChange: handleValueChange,
						onFocus: () => setIsOpen(true),
						onBlur: () => setIsOpen(false),
					}}
					leftAccessory={
						<span className={css(styleSheet.searchIcon)}>
							<SearchIcon fillColor={navigation} />
						</span>
					}
					onItemSelected={handleTagSelected}
					resultsLimit={25}
					pageSize={25}
					type={Api.ResourceAutoCompleteViewModelType.Tag}
					openOnFocus={Boolean(emptyResults)}
					isOpen={Boolean(emptyResults) || isOpen}
					emptyResults={emptyResults}
					sectionTitleElement={
						<div className={css(styleSheet.sectionWrap)}>
							<p className={css(styleSheet.sectionTitle)}>Top Results</p>
						</div>
					}
				/>
			</div>
		</div>
	);
};
